import React, { useEffect } from "react";
import _ from "lodash";
import { useI18n } from "Hooks";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";

import { Modal, IconButton } from "@toddle-edu/ds-web";
import {
  CloseOutlined,
  ArrowOutOutlined,
  LinkOutlined,
} from "@toddle-edu/ds-icons";
import classes from "./ProductUpdatesV2Modal.scss";
import classNames from "classnames";
import {
  setProductUpdateModalOpen,
  setProductUpdateModalVisible,
  setToastMsg,
  setProductUpdateDirectLinkOpen,
} from "Login/modules/LoginModule";
import {
  getProductUpdatesQuery,
  getProductUpdateQuery,
} from "ProductUpdatesV2/modules/ProductUpdatesV2Queries";
import { getOrganizationId } from "modules/CommonSelectors";
import { formatDate, copyToClipboard } from "Utils";
import ProductUpdatesV2Item from "../ProductUpdatesV2Item/ProductUpdatesV2Item";
import {
  resetProductUpdateReadCount,
  getProductUpdateModulesEnhancer,
} from "../../modules/ProductUpdatesV2Modules";
import {
  goToPlatformHomeChildRoute,
  updateQueryParameters,
} from "modules/NavigationModule";
const modalStyles = {
  padding: "0",
};

const ProductUpdatesV2Modal = props => {
  const {
    setProductUpdateModalOpen,
    setProductUpdateModalVisible,
    setProductUpdateDirectLinkOpen,
    productUpdatesFeed,
    networkStatus,
    resetProductUpdateReadCount,
    filters,
    goToPlatformHomeChildRoute,
    productUpdateModalId,
    updateQueryParameters,
    setToastMsg,
    isToddleSupportOrDevUser,
  } = props;
  const { t } = useI18n(["common"]);
  const testId = "ProductUpdatesV2Modal";

  const isModalVisible = !_.isEmpty(productUpdatesFeed) && networkStatus === 7;

  useEffect(() => {
    setProductUpdateModalVisible(isModalVisible);
    return () => {
      setProductUpdateModalVisible(false);
    };
  }, [isModalVisible, setProductUpdateModalVisible]);

  const resetProductUpdateNewContentMutation = () => {
    resetProductUpdateReadCount({ filters });
  };

  const onCloseModal = () => {
    if (productUpdateModalId) {
      updateQueryParameters({
        params: { type: undefined, productId: undefined },
      });
    }
    resetProductUpdateNewContentMutation();
    setProductUpdateModalOpen(false);
    setProductUpdateDirectLinkOpen(false);
  };

  const onIconClick = () => {
    goToPlatformHomeChildRoute({
      route: "productUpdates",
    });
    setProductUpdateModalOpen(false);
    setProductUpdateDirectLinkOpen(false);
  };

  return isModalVisible ? (
    <Modal width="800px" height={"fit-content"} dsVersion="2.0">
      <Modal.Header isClosable={false} hasSeparator={true} style={modalStyles}>
        <div
          className={classes.headerContainer}
          data-test-id="ProductUpdatesV2Modal"
        >
          <h6>{t("common:product_updates")}</h6>
          <div className={classes.iconContainer}>
            <IconButton
              variant="plain"
              onClick={onIconClick}
              icon={<ArrowOutOutlined variant="subtle" size="xx-small" />}
              size={"x-small"}
            />
            <IconButton
              variant="plain"
              onClick={onCloseModal}
              icon={<CloseOutlined variant="subtle" size="xx-small" />}
              size={"x-small"}
            />
          </div>
        </div>
      </Modal.Header>
      <Modal.Body isScrollable={true} style={modalStyles}>
        {_.map(productUpdatesFeed, (productUpdateFeedItem, index) => {
          const groupedByModuleAndVersion = _.groupBy(
            _.get(productUpdateFeedItem, "releaseItems"),
            item =>
              `${_.get(item, "module.moduleKey")}-${_.get(
                item,
                "version"
              )}-${_.get(item, "isBeta")}`
          );

          const groupedProductUpdateItems = _.sortBy(
            _.flatMap(groupedByModuleAndVersion, (items, key) => {
              const [moduleLabel, version, isBeta] = key.split("-");
              return {
                moduleLabel,
                version,
                isBeta,
                releaseItems: _.sortBy(items, "displaySequence"), // Sort items by displaySequence
              };
            }),
            ["moduleLabel", "isBeta"]
          );

          const copyLink = () => {
            const origin = window.location?.origin;
            const search = window.location?.search;

            let copyUrl;
            if (!search) {
              copyUrl = `${origin}/platform?type=product_updates&productId=${_.get(
                productUpdateFeedItem,
                "id"
              )}`;
            } else {
              copyUrl = window.location?.href;
            }

            copyToClipboard(copyUrl, () => {
              setToastMsg({
                msg: "toastMsgs:link_copy_success",
                type: "success",
                position: "toast-bottom-left",
              });
            });
          };

          return (
            <div
              id={`productUpdateItem-${index}`}
              className={classes.itemContainer}
            >
              <div className={classes.bodyContainer}>
                <div className={classes.releaseTitleContainer}>
                  <h5>
                    {formatDate({
                      date: _.get(productUpdateFeedItem, "releaseDate"),
                      format: "DD MMMM, YYYY",
                    })}
                  </h5>
                  <div
                    className={classNames(
                      "text-body",
                      classes.subtitleTextColor
                    )}
                  >
                    {_.get(productUpdateFeedItem, "title")}
                  </div>
                </div>
                <IconButton
                  dsVersion={"2.0"}
                  size={"medium"}
                  variant={"neutral"}
                  type={"outlined"}
                  icon={<LinkOutlined size={"xx-small"} />}
                  onClick={copyLink}
                />
              </div>
              {_.map(groupedProductUpdateItems, productUpdateItem => {
                return (
                  <div>
                    <ProductUpdatesV2Item
                      productUpdateItem={productUpdateItem}
                      isToddleSupportOrDevUser={isToddleSupportOrDevUser}
                      testId={`${testId}-ProductUpdatesV2Item`}
                      productUpdateId={_.get(productUpdateFeedItem, "id")}
                      sourceFrom={"modal"}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </Modal.Body>
    </Modal>
  ) : null;
};

const mapStateToProps = (state, ownProps) => {
  const organizationId = getOrganizationId(state);
  const userCurriculumPrograms = _.get(ownProps, "userCurriculumPrograms", []);
  const userCurriculumProgramIds = _.map(
    userCurriculumPrograms,
    ({ id }) => id
  );
  const isToddleSupportOrDevUser = _.get(
    state,
    "login.userInfo.isDevUser",
    false
  );

  const allModuleIds = _.map(_.get(ownProps, "modules", []), "id");

  return {
    // productUpdateModalId will be present if user has copied link and opened it
    productUpdateModalId: _.get(state, "login.productUpdateModalId", ""),
    isToddleSupportOrDevUser,
    productUpdatesQueryVariables: {
      onlyNewContent: true,
      first: 3,
      filters: {
        organizationId,
        moduleIds: [],
        searchText: "",
        curriculumIds: userCurriculumProgramIds,
        allModuleIds,
      },
    },
    filters: {
      organizationId,
      curriculumIds: userCurriculumProgramIds,
      allModuleIds,
    },
    isData: true,
  };
};

const mapDispatchToProps = {
  goToPlatformHomeChildRoute,
  setProductUpdateModalOpen,
  setProductUpdateModalVisible,
  resetProductUpdateReadCount,
  updateQueryParameters,
  setToastMsg,
  setProductUpdateDirectLinkOpen,
};

export default compose(
  getProductUpdateModulesEnhancer(),
  connect(mapStateToProps, mapDispatchToProps),
  graphql(getProductUpdatesQuery, {
    name: "productUpdates",
    skip: ({ productUpdateModalId, isLoading }) =>
      productUpdateModalId || isLoading,
    options: ({ productUpdatesQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ...productUpdatesQueryVariables,
      },
    }),
    props: ({ productUpdates, ownProps: { isData, isLoading } }) => {
      const productUpdatesData = _.get(productUpdates, "cms", {});

      const productUpdatesFeed = _.map(
        _.get(productUpdatesData, "productUpdatesFeedV2.edges", []),
        "node"
      );
      const networkStatus = productUpdates?.networkStatus;

      return {
        productUpdatesFeed,
        isData: isData && !_.isEmpty(productUpdatesData),
        networkStatus,
        isLoading: networkStatus === 1 || networkStatus === 2 || isLoading,
      };
    },
  }),
  graphql(getProductUpdateQuery, {
    // query to get single product update
    name: "productUpdate",
    skip: ({ productUpdateModalId }) => !productUpdateModalId,
    options: ({ productUpdateModalId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: productUpdateModalId,
      },
    }),
    props: ({ productUpdate }) => {
      const productUpdateNode = _.get(productUpdate, "node", {});
      return {
        networkStatus: _.get(productUpdate, "networkStatus"),
        productUpdatesFeed: _.toArray({ productUpdateNode }),
      };
    },
  })
)(ProductUpdatesV2Modal);
